import {
  faCartFlatbed,
  faCircleCheck,
  faEllipsis,
  faPaperPlane,
  faPercent,
  faReceipt,
  faRotateLeft,
  faSackDollar,
  faWallet,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { InfoTooltip } from '@shared/components/ui/InfoTooltip';
import { cn } from '@shared/utils/utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import type { ProfitabilitySummaryResponse } from '../../models/Profitability';
import { formatCountWithShare, formatProfitabilityAmount, formatProfitabilityPercent } from '../../utils/formatMoney';
import { computeTotalCosts } from '../../utils/profitabilityCosts';
import { PROFITABILITY_SUMMARY_GRID, ProfitabilityCostsChart } from './ProfitabilityCostsChart';

interface ProfitabilityKpiSectionProps {
  data?: ProfitabilitySummaryResponse;
  isLoading?: boolean;
  locale: string;
  onOpenOrders?: () => void;
  onOpenCogs?: () => void;
  onOpenShipping?: () => void;
}

interface KpiCard {
  title: string;
  display: string;
  icon: IconDefinition;
  iconBg: string;
  iconColor: string;
  testId: string;
  tooltip?: string;
  signed?: boolean;
  rawValue?: number | null;
  onClick?: () => void;
}

interface KpiRow {
  cards: KpiCard[];
  gridClass: string;
}

function computeOtherOrderStates(data: ProfitabilitySummaryResponse): number | null {
  const shipped = data.order_counts?.shipped;
  if (shipped == null) return null;

  const delivered = data.logistic_counts?.delivered ?? 0;
  const returned = data.logistic_counts?.returned ?? 0;

  if (data.logistic_counts != null) {
    return Math.max(0, shipped - delivered - returned);
  }

  return null;
}

function KpiCardSkeleton() {
  return (
    <div className="animate-pulse rounded-xl border border-border bg-card-bg p-4">
      <div className="mb-3 flex items-center gap-2">
        <div className="h-8 w-8 rounded-lg bg-muted/60" />
        <div className="h-3 w-24 rounded bg-muted/60" />
      </div>
      <div className="h-7 w-28 rounded bg-muted/60" />
    </div>
  );
}

function KpiCardItem({ kpi, isLoading }: { kpi: KpiCard; isLoading?: boolean }) {
  const isNegative = kpi.signed && kpi.rawValue != null && kpi.rawValue < 0;
  const valueClass = kpi.signed ? (isNegative ? 'text-destructive' : 'text-success') : 'text-foreground';
  const clickable = !!kpi.onClick && !isLoading;

  return (
    <div
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? kpi.onClick : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                kpi.onClick?.();
              }
            }
          : undefined
      }
      className={cn(
        'group relative rounded-xl border border-border bg-card-bg p-4 shadow-sm transition-all',
        'hover:border-primary/20 hover:shadow-md',
        clickable && 'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      )}
      data-testid={kpi.testId}
    >
      <div className="mb-3 flex items-center gap-2">
        <span className={cn('flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg', kpi.iconBg)}>
          <FontAwesomeIcon icon={kpi.icon} className={cn('h-4 w-4', kpi.iconColor)} aria-hidden />
        </span>
        <div className="flex min-w-0 flex-1 items-center gap-1">
          <p className="truncate text-xs font-medium leading-tight text-muted-foreground">{kpi.title}</p>
          {kpi.tooltip && (
            <InfoTooltip text={kpi.tooltip} dataTestId={`${kpi.testId}-tooltip`} iconClassName="w-3 h-3 shrink-0" />
          )}
        </div>
      </div>

      <p
        className={cn(
          'truncate text-xl font-semibold tracking-tight tabular-nums',
          isLoading ? 'text-muted-foreground' : valueClass,
        )}
      >
        {isLoading ? '…' : kpi.display}
      </p>
    </div>
  );
}

export const ProfitabilityKpiSection = ({
  data,
  isLoading,
  locale,
  onOpenOrders,
  onOpenCogs,
  onOpenShipping,
}: ProfitabilityKpiSectionProps) => {
  const { t } = useTranslation('profitabilityModule');
  const currency = useCurrency();

  const kpiRows = useMemo((): { mainRow: KpiRow; fulfillmentRow: KpiRow } => {
    const orderCounts = data?.order_counts;
    const logisticCounts = data?.logistic_counts;
    const shippedForShare = orderCounts?.shipped;

    const shippedDisplay = orderCounts?.shipped != null ? String(orderCounts.shipped) : '—';
    const deliveredShare =
      logisticCounts != null ? formatCountWithShare(logisticCounts.delivered, shippedForShare, locale) : '—';
    const returnedShare =
      logisticCounts != null ? formatCountWithShare(logisticCounts.returned, shippedForShare, locale) : '—';
    const otherStatesCount = data ? computeOtherOrderStates(data) : null;
    const otherStatesShare = formatCountWithShare(otherStatesCount, shippedForShare, locale);

    const marginTooltip = t('summary.kpis.margin_tooltip', {
      cogs: data ? formatProfitabilityAmount(data.totals.recognized_cogs, locale, currency) : '—',
      shipping: data ? formatProfitabilityAmount(data.totals.recognized_logistics, locale, currency) : '—',
      adSpend: data ? formatProfitabilityAmount(data.totals.ad_spend, locale, currency) : '—',
    });

    const totalCosts = data ? computeTotalCosts(data.totals) : null;

    const mainRow: KpiRow = {
      gridClass: PROFITABILITY_SUMMARY_GRID,
      cards: [
        {
          title: t('summary.kpis.revenue'),
          display: data ? formatProfitabilityAmount(data.totals.recognized_revenue, locale, currency) : '—',
          icon: faSackDollar,
          iconBg: 'bg-emerald-500/10',
          iconColor: 'text-emerald-500',
          testId: 'profitability-kpi-revenue',
        },
        {
          title: t('summary.kpis.profit'),
          display: data ? formatProfitabilityAmount(data.totals.profit, locale, currency) : '—',
          icon: faWallet,
          iconBg: 'bg-violet-500/10',
          iconColor: 'text-violet-500',
          testId: 'profitability-kpi-profit',
          tooltip: t('summary.kpis.profit_tooltip'),
          signed: true,
          rawValue: data?.totals.profit,
        },
        {
          title: t('summary.kpis.total_costs'),
          display: totalCosts != null ? formatProfitabilityAmount(totalCosts, locale, currency) : '—',
          icon: faReceipt,
          iconBg: 'bg-rose-500/10',
          iconColor: 'text-rose-500',
          testId: 'profitability-kpi-total-costs',
          tooltip: t('summary.kpis.total_costs_tooltip'),
        },
        {
          title: t('summary.kpis.margin'),
          display: data ? formatProfitabilityPercent(data.averages.margin_percent, locale) : '—',
          icon: faPercent,
          iconBg: 'bg-violet-500/10',
          iconColor: 'text-violet-500',
          testId: 'profitability-kpi-margin',
          tooltip: marginTooltip,
          signed: true,
          rawValue: data?.averages.margin_percent,
        },
        {
          title: t('summary.kpis.orders'),
          display: orderCounts != null ? String(orderCounts.total) : data ? String(data.orders_count) : '—',
          icon: faCartFlatbed,
          iconBg: 'bg-sky-500/10',
          iconColor: 'text-sky-500',
          testId: 'profitability-kpi-orders',
          tooltip: t('summary.kpis.orders_tooltip'),
          onClick: onOpenOrders,
        },
      ],
    };

    const fulfillmentRow: KpiRow = {
      gridClass: 'grid grid-cols-2 gap-3 sm:grid-cols-4',
      cards: [
        {
          title: t('summary.kpis.shipped'),
          display: shippedDisplay,
          icon: faPaperPlane,
          iconBg: 'bg-emerald-500/10',
          iconColor: 'text-emerald-500',
          testId: 'profitability-kpi-shipped',
          tooltip: t('summary.kpis.shipped_tooltip'),
        },
        {
          title: t('summary.kpis.delivered'),
          display: deliveredShare,
          icon: faCircleCheck,
          iconBg: 'bg-emerald-500/10',
          iconColor: 'text-emerald-500',
          testId: 'profitability-kpi-delivered',
          tooltip: t('summary.kpis.delivered_tooltip'),
        },
        {
          title: t('summary.kpis.returned'),
          display: returnedShare,
          icon: faRotateLeft,
          iconBg: 'bg-amber-500/10',
          iconColor: 'text-amber-500',
          testId: 'profitability-kpi-returned',
          tooltip: t('summary.kpis.returned_tooltip'),
        },
        {
          title: t('summary.kpis.other_states'),
          display: otherStatesShare,
          icon: faEllipsis,
          iconBg: 'bg-muted/40',
          iconColor: 'text-muted-foreground',
          testId: 'profitability-kpi-other-states',
          tooltip: t('summary.kpis.other_states_tooltip'),
        },
      ],
    };

    return { mainRow, fulfillmentRow };
  }, [currency, data, locale, onOpenOrders, t]);

  if (isLoading) {
    return (
      <div className="space-y-4" data-testid="profitability-kpis">
        <div className={cn('grid gap-3', kpiRows.mainRow.gridClass)}>
          {kpiRows.mainRow.cards.map((_, i) => (
            <KpiCardSkeleton key={i} />
          ))}
        </div>
        <ProfitabilityCostsChart isLoading locale={locale} onOpenCogs={onOpenCogs} onOpenShipping={onOpenShipping} />
        <div className={cn('grid gap-3', kpiRows.fulfillmentRow.gridClass)}>
          {kpiRows.fulfillmentRow.cards.map((_, i) => (
            <KpiCardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="profitability-kpis">
      <div className={cn('grid gap-3', kpiRows.mainRow.gridClass)}>
        {kpiRows.mainRow.cards.map((kpi) => (
          <KpiCardItem key={kpi.testId} kpi={kpi} />
        ))}
      </div>

      <ProfitabilityCostsChart data={data} locale={locale} onOpenCogs={onOpenCogs} onOpenShipping={onOpenShipping} />

      <div className={cn('grid gap-3', kpiRows.fulfillmentRow.gridClass)}>
        {kpiRows.fulfillmentRow.cards.map((kpi) => (
          <KpiCardItem key={kpi.testId} kpi={kpi} />
        ))}
      </div>
    </div>
  );
};
